import { API_URL } from "@/api/client";
import { ColumnDef } from "@tanstack/react-table";
import { Badge } from "../../components/ui/badge";
import * as health_utils from "./health_utils";

const ALERT_TOOLTIP = "Green = No Firing/Unsnoozed Alerts,\nYellow = No L0 Alerts,\nAmber = <=1 L0 Alert,\nRed = >1 L0 Alert";
const apiURL = new URL(API_URL);

export const AssetHealthDataColumns = (): ColumnDef<any>[] => [
    {
        id: "asset",
        header: "Asset",
        cell: ({ row }) => {
            return (
                <div className="flex flex-col p-2 bg-gray-100 rounded-md">
                    <div className="lg:text-lg truncate text-ellipsis font-semibold">
                        {row.original.current_installation_location}
                    </div>
                    <div className="text-sm truncate text-ellipsis">
                        {row.original.current_installation_name}
                    </div>
                </div>
            );
        },
    },
    {
        id: "serial_number",
        accessorKey: "sensor_kit",
        enableSorting: true,
        header: "Sensor Kit",
        cell: ({ row }) => {
            return (
                <div className="flex flex-col p-2 bg-gray-100 rounded-md">
                    <div className="lg:text-lg truncate text-ellipsis font-semibold">
                        {row.original.sensor_kit}
                    </div>
                </div>
            );
        },
    },
    {
        id: "firing_alerts_L0",
        accessorKey: "alert_count_L0",
        enableSorting: true,
        header: "L0 Alerts",
        cell: ({ row }) => {
            const sensorKit = row.original.id;
            const alertCountL0 = row.original.alert_count_L0;
            const url = new URL(`/admin/alerts/sensorkitalert/?active__exact=1&alert_level__exact=L0&alert_state__exact=firing&sensor_kit__id__exact=${sensorKit}`, apiURL);
            return (
                <div className="flex flex-col p-2 bg-gray-100 rounded-md">
                    <a href={url.href} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                        {alertCountL0}
                    </a>
                </div>
            );
        },
    },
    {
        id: "firing_alerts_L1",
        accessorKey: "alert_count_L1",
        enableSorting: true,
        header: "L1 Alerts",
        cell: ({ row }) => {
            const sensorKit = row.original.id;
            const alertCountL1 = row.original.alert_count_L1;
            const url = new URL(`/admin/alerts/sensorkitalert/?active__exact=1&alert_level__exact=L1&alert_state__exact=firing&sensor_kit__id__exact=${sensorKit}`, apiURL);
            return (
                <div className="flex flex-col p-2 bg-gray-100 rounded-md">
                    <a href={url.href} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                        {alertCountL1}
                    </a>
                </div>
            );
        },
    },
    {
        id: "firing_alerts_L2",
        accessorKey: "alert_count_L2",
        enableSorting: true,
        header: "L2 Alerts",
        cell: ({ row }) => {
            const sensorKit = row.original.id;
            const alertCountL2 = row.original.alert_count_L2;
            const url = new URL(`/admin/alerts/sensorkitalert/?active__exact=1&alert_level__exact=L2&alert_state__exact=firing&sensor_kit__id__exact=${sensorKit}`, apiURL);
            return (
                <div className="flex flex-col p-2 bg-gray-100 rounded-md">
                    <a href={url.href} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                        {alertCountL2}
                    </a>
                </div>
            );
        },
    },
    {
        id: "firing_alerts_count",
        accessorKey: "alert_count",
        enableSorting: true,
        header: "Total Alerts",
        cell: ({ row }) => {
            return (
                <div className="flex flex-col p-2 bg-gray-100 rounded-md">
                    <div className="lg:text-lg truncate text-ellipsis font-semibold">
                        {row.original.alert_count}
                    </div>
                </div>
            );
        },
    },
    {
        id: "grafana_link",
        header: "Grafana",
        cell: ({ row }) => {
            const sensorKit = row.original.sensor_kit;
            const version = row.original.version;
            let grafanaUrl = row.original.grafana_url;

            if (/^2(\.0|\.\d+)?$/.test(version)) {
                grafanaUrl = `${grafanaUrl}&var-Nodes=${sensorKit}&from=now-3h&to=now`;
            } else {
                grafanaUrl = `${grafanaUrl}&var-node=${sensorKit}&from=now-3h&to=now`;
            }
            return (
                <div className="flex flex-col p-2 rounded-md">
                    <a href={grafanaUrl} target="_blank" rel="noopener noreferrer">
                        <img src="https://upload.wikimedia.org/wikipedia/commons/a/a1/Grafana_logo.svg" alt="Grafana" className="w-6 h-6" />
                    </a>
                </div>
            );
        },
    },
    {
        id: "balena_link",
        header: "Balena",
        cell: ({ row }) => {
            const version = row.original.version;
            let balenaUrl;

            if (/^2(\.0|\.\d+)?$/.test(version)) {
                balenaUrl = `https://dashboard.balena-cloud.com/fleets/2014592`;
            } else {
                balenaUrl = `https://dashboard.balena-cloud.com/fleets/2117070/devices`;
            }
            return (
                <div className="flex flex-col p-2 rounded-md">
                    <a href={balenaUrl} target="_blank" rel="noopener noreferrer">
                        <img src="https://raw.githubusercontent.com/balena-io/etcher/master/assets/icon.png" alt="Balena" className="w-6 h-6" style={{ filter: 'grayscale(75%)' }} />
                    </a>
                </div>
            );
        },
    },
    {
        id: "gary_status",
        accessorKey: "gary_status",
        enableSorting: true,
        header: "Health status",
        cell: ({ row }) => {
            const status = row.original.gary_status;
            let colorClass;
            switch (status) {
                case 0:
                    colorClass = health_utils.COLORS.GREEN;
                    break;
                case 1:
                    colorClass = health_utils.COLORS.YELLOW;
                    break;
                case 2:
                    colorClass = health_utils.COLORS.AMBER;
                    break;
                case 3:
                    colorClass = health_utils.COLORS.RED;
                    break;
                default:
                    colorClass = '';
            }
            return (
                <div className="flex flex-col p-2 rounded-md">
                    <div className={`lg:text-lg truncate text-ellipsis`}>
                        <Badge className={`lg:text-lg ${colorClass}`}>◯</Badge>
                    </div>
                </div>
            );
        },
        meta: {
            className: "col-span-7 lg:col-span-7",
            tooltip: ALERT_TOOLTIP,
        },
    },
];